import {
  IProductDetail,
  IProductStatstics,
  PRODUCT_ACTIVATION_STATUS,
  PRODUCT_TYPES,
} from "types/marketpleace/product.interface";
import { IDiscountTemplate } from "types/marketpleace/discount.interface";
import { ICombination } from "types/marketpleace/combination.interface";
import { demoProductBanner } from "./demoImages";
import { mockAttributes } from "./mockShopListings";
import { mockProducts } from "./mockProducts";
import { demoApi } from "./demoLocale";

const sizeAttr = mockAttributes[0];
const colorAttr = mockAttributes[1];

const findMockProductListing = (productId?: string) =>
  mockProducts.find((product) => product.ProductId === productId) ?? mockProducts[0];

const buildCombinations = (
  productId: string,
  gross: number,
  amount: number,
  type: PRODUCT_TYPES
): ICombination[] => {
  const netto = Math.round((gross / 1.23) * 100) / 100;
  const tax = 23;
  const suffix = productId.slice(-3);

  if (type === PRODUCT_TYPES["Elektroniczny"]) {
    return [
      {
        CombinationId: `00000000-0000-0000-0000-00000000e${suffix}`,
        Tax: tax,
        Netto: netto,
        Gross: gross,
        Amount: 999,
        EAN: `59000000${suffix}`,
        Discount: null,
        Values: [],
      },
    ];
  }

  const sizes = sizeAttr.Values.slice(0, 3);
  return sizes.map((size, index) => {
    const color = colorAttr.Values[index % colorAttr.Values.length];
    return {
      CombinationId: `00000000-0000-0000-0000-00000000c${suffix}${index}`,
      Tax: tax,
      Netto: netto,
      Gross: gross,
      Amount: Math.max(1, Math.floor(amount / sizes.length) - index),
      EAN: `5901234${suffix}${index}`,
      Discount:
        index === 0
          ? {
              Percent: 10,
              Tax: tax,
              Gross: Math.round(gross * 0.9 * 100) / 100,
              Netto: Math.round(netto * 0.9 * 100) / 100,
              From: new Date("2026-06-01"),
              To: new Date("2026-12-31"),
            }
          : null,
      Values: [
        {
          AttributeId: sizeAttr.AttributeId,
          AttributeName: sizeAttr.Name,
          ValueId: size.ValueId,
          ValueName: size.Name,
        },
        {
          AttributeId: colorAttr.AttributeId,
          AttributeName: colorAttr.Name,
          ValueId: color.ValueId,
          ValueName: color.Name,
        },
      ],
    };
  });
};

const buildProductDetail = (productId?: string): IProductDetail => {
  const listing = findMockProductListing(productId);
  const suffix = listing.ProductId.slice(-3);

  return {
    ProductId: listing.ProductId,
    Name: listing.Name,
    Description:
      `${listing.Name} — produkt demo akademii. ${listing.ShortDescription}. ` +
      `Kategoria: ${listing.CategoryName}. Dostępny w sklepie JustWin.`,
    ShortDescription: listing.ShortDescription,
    Tag: listing.CategoryName.toLowerCase(),
    SystemStatus: listing.SystemStatus,
    Status: listing.CurrentStatus,
    BannerPhotoFilePath: listing.BannerPhotoFilePath,
    Type: listing.Type,
    Seller: {
      AcademyId: listing.AcademyId,
      SellerId: listing.SellerId,
      Name: listing.SellerName,
    },
    Category: {
      CategoryId: listing.CategoryId,
      Name: listing.CategoryName,
    },
    Combinations: buildCombinations(
      listing.ProductId,
      listing.Gross,
      listing.Amount,
      listing.Type
    ),
    Photos: [
      {
        ProductPhotoFileId: `00000000-0000-0000-0000-00000000p${suffix}1`,
        FilePath: listing.BannerPhotoFilePath,
      },
      {
        ProductPhotoFileId: `00000000-0000-0000-0000-00000000p${suffix}2`,
        FilePath: demoProductBanner(`${listing.ProductId}-alt`),
      },
    ],
    AdditionalFiles: [
      {
        AdditionalFileId: `00000000-0000-0000-0000-00000000f${suffix}1`,
        Name: "Specyfikacja produktu.pdf",
        FilePath: "#",
      },
      {
        AdditionalFileId: `00000000-0000-0000-0000-00000000f${suffix}2`,
        Name: "Instrukcja użytkowania.pdf",
        FilePath: "#",
      },
    ],
    Visibility: 1,
    AcademyId: listing.AcademyId,
  };
};

const productDetailsById: Record<string, IProductDetail> = Object.fromEntries(
  mockProducts.map((product) => [product.ProductId, buildProductDetail(product.ProductId)])
);

const discountTemplatesByAcademy: Record<string, IDiscountTemplate[]> = {};

const ensureDiscounts = (academyId: string): IDiscountTemplate[] => {
  if (!discountTemplatesByAcademy[academyId]) {
    discountTemplatesByAcademy[academyId] = [
      {
        DiscountTemplateId: `00000000-0000-0000-0000-00000000d${academyId.slice(-2)}1`,
        Name: "Rabat sezonowy 10%",
        Percent: 10,
        Start: new Date("2026-06-01"),
        End: new Date("2026-08-31"),
        AcademyId: academyId,
      },
      {
        DiscountTemplateId: `00000000-0000-0000-0000-00000000d${academyId.slice(-2)}2`,
        Name: "Promocja klubowa 15%",
        Percent: 15,
        Start: new Date("2026-07-01"),
        End: new Date("2026-12-31"),
        AcademyId: academyId,
      },
    ];
  }
  return discountTemplatesByAcademy[academyId];
};

export const getMockProductDetail = demoApi(async (
  productId?: string,
  _academyId?: string
): Promise<IProductDetail> =>
  productDetailsById[productId ?? ""] ?? buildProductDetail(productId));

export const getMockProductStatistics = demoApi(async (
  _academyId?: string,
  productId?: string,
  _start?: Date,
  _end?: Date
): Promise<IProductStatstics> => {
  const detail = await getMockProductDetail(productId);
  const soldCount = Math.max(3, Math.min(28, detail.Combinations.reduce((sum, c) => sum + c.Amount, 0) % 30));
  const soldAmount = Math.round(soldCount * (detail.Combinations[0]?.Gross ?? 50) * 100) / 100;

  return {
    Name: detail.Name,
    SoldCount: soldCount,
    SoldAmount: soldAmount,
    RefundCount: 1,
    RefundAmount: detail.Combinations[0]?.Gross ?? 0,
    AttributeSoldDetails: detail.Combinations.slice(0, 3).map((combination, index) => ({
      AttributeName:
        combination.Values.map((value) => value.ValueName).join(" / ") || "Standard",
      EAN: combination.EAN,
      Netto: combination.Netto,
      Gross: combination.Gross,
      Tax: combination.Tax,
      SoldCount: Math.max(1, soldCount - index * 2),
    })),
    OrderDetails: [
      {
        OrderId: "00000000-0000-0000-0000-000000000401",
        Created: new Date("2026-06-15T10:00:00Z"),
        Number: "ORD-2026-001",
      },
      {
        OrderId: "00000000-0000-0000-0000-000000000402",
        Created: new Date("2026-06-22T14:30:00Z"),
        Number: "ORD-2026-014",
      },
      {
        OrderId: "00000000-0000-0000-0000-000000000403",
        Created: new Date("2026-07-03T09:15:00Z"),
        Number: "ORD-2026-028",
      },
    ],
  };
});

export const getMockDiscountTemplates = demoApi(async (
  academyId?: string
): Promise<IDiscountTemplate[]> => ensureDiscounts(academyId || mockProducts[0].AcademyId));

export const mockProductMutationSuccess = async (..._args: any[]) => ({ Success: true });

export const changeMockProductState = async (
  productId: string,
  _academyId: string,
  status: PRODUCT_ACTIVATION_STATUS
) => {
  const detail = productDetailsById[productId] ?? buildProductDetail(productId);
  detail.Status = status;
  productDetailsById[productId] = detail;

  const listing = mockProducts.find((product) => product.ProductId === productId);
  if (listing) {
    listing.CurrentStatus = status;
  }

  return { Success: true };
};

export const saveMockDiscountTemplate = async (template: {
  Name: string;
  Percent: number;
  Start: Date;
  End: Date;
  AcademyId: string;
  DiscountTemplateId?: string;
}) => {
  const list = ensureDiscounts(template.AcademyId);
  if (template.DiscountTemplateId) {
    const index = list.findIndex((item) => item.DiscountTemplateId === template.DiscountTemplateId);
    if (index >= 0) {
      list[index] = { ...list[index], ...template, DiscountTemplateId: template.DiscountTemplateId };
      return { Success: true };
    }
  }

  list.push({
    ...template,
    DiscountTemplateId: `00000000-0000-0000-0000-00000000d${Date.now().toString().slice(-4)}`,
  });
  return { Success: true };
};

export const removeMockDiscountTemplate = async (academyId: string, discountTemplateId: string) => {
  const list = ensureDiscounts(academyId);
  discountTemplatesByAcademy[academyId] = list.filter(
    (item) => item.DiscountTemplateId !== discountTemplateId
  );
  return { Success: true };
};
